import {
  CheckCircle2,
  Download,
  FileSpreadsheet,
  Percent,
  Loader2,
  Lock,
  Building2,
  TrendingUp,
  BarChart3,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';

export function ReportsDashboard() {
  const { user } = useAuth();
  const [selectedQuarter, setSelectedQuarter] = useState('Q2-2026');
  const [isExporting, setIsExporting] = useState(false);
  const [exportComplete, setExportComplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'general' | 'gst'>('general');

  // Tenant subscription validation for GST Compliance Reports
  const tenantId = user?.tenantId;
  const tenantFlags = JSON.parse(
    localStorage.getItem(`tenant_flags_${tenantId}`) ||
    '{"bulkMatrix":false,"gstComplianceExport":false,"loyaltyEngine":false}',
  );
  const hasGstAccess = !!(tenantFlags.gstComplianceExport || user?.role === 'SUPER_ADMIN');

  // States for aggregated reports data
  const [salesSummary, setSalesSummary] = useState({
    totalInvoices: 0,
    grossSales: 0,
    netSales: 0,
    totalDiscount: 0,
    costOfGoodsSold: 0,
    netProfit: 0,
  });
  const [auditSummary, setAuditSummary] = useState({
    grossSales: 0,
    netSales: 0,
    totalGst: 0,
    cgst: 0,
    sgst: 0,
    igst: 0,
    averageTicketValue: 0,
  });

  const [gstSlabAudit, setGstSlabAudit] = useState([
    { rate: 5, taxableValue: 0, cgst: 0, sgst: 0, igst: 0, totalGst: 0 },
    { rate: 12, taxableValue: 0, cgst: 0, sgst: 0, igst: 0, totalGst: 0 },
    { rate: 18, taxableValue: 0, cgst: 0, sgst: 0, igst: 0, totalGst: 0 },
    { rate: 28, taxableValue: 0, cgst: 0, sgst: 0, igst: 0, totalGst: 0 },
  ]);

  const [valuationData, setValuationData] = useState({
    totalItems: 0,
    totalStock: 0,
    costValuation: 0,
    sellingValuation: 0,
    potentialProfit: 0,
  });

  const simulateExport = () => {
    setIsExporting(true);
    setExportComplete(false);
    setTimeout(() => {
      setIsExporting(false);
      setExportComplete(true);
      setTimeout(() => setExportComplete(false), 3000);
    }, 1500);
  };

  const loadReportData = async () => {
    if (!user?.tenantId) return;
    setLoading(true);
    try {
      const headers = { 'x-tenant-id': user.tenantId };
      const [salesRes, gstRes, valRes, invoicesRes] = await Promise.all([
        api.get('/reports/sales-summary', { headers }),
        api.get('/reports/gst', { headers }),
        api.get('/reports/inventory-valuation', { headers }),
        api.get('/billing/invoices', { headers }),
      ]);

      const sales = salesRes.data.data || {
        totalInvoices: 0,
        totalRevenue: 0,
        grossSales: 0,
        netSales: 0,
        totalDiscount: 0,
        costOfGoodsSold: 0,
        netProfit: 0,
      };
      setSalesSummary(sales);
      const gst = gstRes.data.data || {
        cgstTotal: 0,
        sgstTotal: 0,
        igstTotal: 0,
        taxableAmount: 0,
        totalWithTax: 0,
      };
      const valuation = valRes.data.data || {
        totalItems: 0,
        totalStock: 0,
        costValuation: 0,
        sellingValuation: 0,
        potentialProfit: 0,
      };
      const invoices = invoicesRes.data.data || [];

      // Calculate totals
      const totalGst = gst.cgstTotal + gst.sgstTotal + gst.igstTotal;
      const averageTicketValue =
        sales.totalInvoices > 0 ? gst.totalWithTax / sales.totalInvoices : 0;

      setAuditSummary({
        grossSales: gst.totalWithTax,
        netSales: gst.taxableAmount,
        totalGst,
        cgst: gst.cgstTotal,
        sgst: gst.sgstTotal,
        igst: gst.igstTotal,
        averageTicketValue,
      });

      setValuationData(valuation);

      // Aggregate GSTR-1 slabs dynamically
      const slabsMap = {
        5: { taxableValue: 0, cgst: 0, sgst: 0, igst: 0, totalGst: 0 },
        12: { taxableValue: 0, cgst: 0, sgst: 0, igst: 0, totalGst: 0 },
        18: { taxableValue: 0, cgst: 0, sgst: 0, igst: 0, totalGst: 0 },
        28: { taxableValue: 0, cgst: 0, sgst: 0, igst: 0, totalGst: 0 },
      };

      invoices.forEach((inv: any) => {
        inv.items.forEach((item: any) => {
          const rate = Math.round(item.gstRate);
          if (rate === 5 || rate === 12 || rate === 18 || rate === 28) {
            const qty = item.quantity || 0;
            const price = item.unitPrice || 0;
            const discount = item.discount || 0;
            const taxableVal = (price - discount) * qty;

            slabsMap[rate].taxableValue += taxableVal;
            slabsMap[rate].cgst += item.cgstAmount || 0;
            slabsMap[rate].sgst += item.sgstAmount || 0;
            slabsMap[rate].igst += item.igstAmount || 0;
            slabsMap[rate].totalGst +=
              (item.cgstAmount || 0) + (item.sgstAmount || 0) + (item.igstAmount || 0);
          }
        });
      });

      setGstSlabAudit([
        { rate: 5, ...slabsMap[5] },
        { rate: 12, ...slabsMap[12] },
        { rate: 18, ...slabsMap[18] },
        { rate: 28, ...slabsMap[28] },
      ]);
    } catch (err) {
      console.error('Failed to load real-time analytics reports.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReportData();
  }, [user?.tenantId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3 text-slate-400">
          <Loader2 size={32} className="animate-spin text-emerald-500" />
          <p className="text-sm font-medium">Aggregating real-time database audits…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between border-b border-slate-100 pb-4">
        <div>
          <h1 className="text-2xl font-bold text-surface-900">Reports & Analytics</h1>
          <p className="mt-1 text-sm text-surface-500">
            Monitor shop gross performance margins, general business metrics, and file tax compliance records.
          </p>
        </div>

        {/* Calendar quarter filter & export button (Only visible in active GST tab when user has access) */}
        {activeTab === 'gst' && hasGstAccess && (
          <div className="flex gap-2">
            <div className="relative">
              <select
                value={selectedQuarter}
                onChange={(e) => setSelectedQuarter(e.target.value)}
                className="block w-40 pl-3 pr-8 py-1.5 text-xs font-semibold bg-white border border-slate-200 rounded focus:outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 cursor-pointer"
              >
                <option value="Q2-2026">Q2 (Apr - Jun 2026)</option>
                <option value="Q1-2026">Q1 (Jan - Mar 2026)</option>
                <option value="Q4-2025">Q4 (Oct - Dec 2025)</option>
              </select>
            </div>

            <button
              onClick={simulateExport}
              disabled={isExporting}
              className="btn-primary flex items-center gap-1.5 cursor-pointer text-xs font-semibold disabled:opacity-50"
            >
              {isExporting ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>Compiling GSTR-1...</span>
                </>
              ) : exportComplete ? (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                  <span>Downloaded GSTR-1 Sheet</span>
                </>
              ) : (
                <>
                  <Download size={14} />
                  <span>Export Audit Logs</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Tabs Selector Switcher */}
      {hasGstAccess && (
        <div className="flex gap-1 border-b border-slate-250">
          <button
            onClick={() => setActiveTab('general')}
            className={`px-4 py-2.5 text-xs font-bold border-b-2 -mb-[2px] transition-all flex items-center gap-1.5 cursor-pointer ${
              activeTab === 'general'
                ? 'border-emerald-600 text-emerald-650'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <TrendingUp size={14} />
            <span>General Business Analytics</span>
          </button>
          <button
            onClick={() => setActiveTab('gst')}
            className={`px-4 py-2.5 text-xs font-bold border-b-2 -mb-[2px] transition-all flex items-center gap-1.5 cursor-pointer ${
              activeTab === 'gst'
                ? 'border-emerald-600 text-emerald-650'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Building2 size={14} />
            <span>GST Compliance Report</span>
          </button>
        </div>
      )}

      {/* ── Tab Content ── */}
      {activeTab === 'general' ? (
        <div className="space-y-6">
          {/* General Stats summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 animate-fadeIn">
            {/* Gross Turnover */}
            <div className="card flex justify-between items-start border-l-4 border-l-blue-500 bg-white shadow-sm p-4 hover:shadow-md transition-all">
              <div className="space-y-1">
                <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                  Gross Turnover
                </span>
                <p className="text-xl font-bold text-slate-800 font-mono">
                  ₹{salesSummary.grossSales.toLocaleString('en-IN')}
                </p>
                <p className="text-[10px] text-slate-400">Total billing (incl. tax)</p>
              </div>
            </div>

            {/* Net Sales */}
            <div className="card flex justify-between items-start border-l-4 border-l-slate-400 bg-white shadow-sm p-4 hover:shadow-md transition-all">
              <div className="space-y-1">
                <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                  Net Sales Value
                </span>
                <p className="text-xl font-bold text-slate-800 font-mono">
                  ₹{salesSummary.netSales.toLocaleString('en-IN')}
                </p>
                <p className="text-[10px] text-slate-400">Excl. GST taxes</p>
              </div>
            </div>

            {/* Discounts Awarded */}
            <div className="card flex justify-between items-start border-l-4 border-l-amber-500 bg-white shadow-sm p-4 hover:shadow-md transition-all">
              <div className="space-y-1">
                <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                  Discounts Awarded
                </span>
                <p className="text-xl font-bold text-slate-800 font-mono">
                  ₹{salesSummary.totalDiscount.toLocaleString('en-IN')}
                </p>
                <p className="text-[10px] text-slate-400">Promotions & markdowns</p>
              </div>
            </div>

            {/* Realized Profit */}
            <div className="card flex justify-between items-start border-l-4 border-l-emerald-600 bg-emerald-50/50 shadow-sm p-4 hover:shadow-md transition-all">
              <div className="space-y-1">
                <span className="text-[10px] text-emerald-800 font-bold uppercase tracking-wider">
                  Realized Net Profit
                </span>
                <p className="text-xl font-bold text-emerald-700 font-mono">
                  ₹{salesSummary.netProfit.toLocaleString('en-IN')}
                </p>
                <p className="text-[10px] text-emerald-600 font-medium">Net Sales - COGS</p>
              </div>
            </div>

            {/* Capital Invested */}
            <div className="card flex justify-between items-start border-l-4 border-l-indigo-600 bg-indigo-50/30 shadow-sm p-4 hover:shadow-md transition-all">
              <div className="space-y-1">
                <span className="text-[10px] text-indigo-800 font-bold uppercase tracking-wider">
                  Capital Invested
                </span>
                <p className="text-xl font-bold text-indigo-750 font-mono">
                  ₹{valuationData.costValuation.toLocaleString('en-IN')}
                </p>
                <p className="text-[10px] text-indigo-600 font-medium">Cost of stock in hand</p>
              </div>
            </div>

            {/* Stock Valuation (Retail) */}
            <div className="card flex justify-between items-start border-l-4 border-l-purple-500 bg-purple-50/30 shadow-sm p-4 hover:shadow-md transition-all">
              <div className="space-y-1">
                <span className="text-[10px] text-purple-800 font-bold uppercase tracking-wider">
                  Stock Value (Retail)
                </span>
                <p className="text-xl font-bold text-purple-700 font-mono">
                  ₹{valuationData.sellingValuation.toLocaleString('en-IN')}
                </p>
                <p className="text-[10px] text-purple-600 font-medium">Selling value of stock</p>
              </div>
            </div>
          </div>

          {/* Grid Layout for General Info */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fadeIn">
            {/* Sales performance details card */}
            <div className="card">
              <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4 flex items-center gap-1.5">
                <BarChart3 size={15} className="text-emerald-600" />
                <span>Sales Performance Summary</span>
              </h3>
              <div className="divide-y divide-slate-100 text-xs space-y-2.5">
                <div className="py-2.5 flex justify-between items-center">
                  <span className="font-semibold text-slate-700">Total Invoices generated</span>
                  <span className="font-mono text-slate-650 font-bold">{salesSummary.totalInvoices} Transactions</span>
                </div>
                <div className="py-2.5 flex justify-between items-center">
                  <span className="font-semibold text-slate-700">Gross Sales Volume</span>
                  <span className="font-mono text-slate-650 font-bold">₹{salesSummary.grossSales.toLocaleString('en-IN')}</span>
                </div>
                <div className="py-2.5 flex justify-between items-center">
                  <span className="font-semibold text-slate-700">Net Taxable Revenue</span>
                  <span className="font-mono text-slate-650 font-bold">₹{salesSummary.netSales.toLocaleString('en-IN')}</span>
                </div>
                <div className="py-2.5 flex justify-between items-center">
                  <span className="font-semibold text-slate-700">Discounts Awarded</span>
                  <span className="font-mono text-slate-650 font-bold">₹{salesSummary.totalDiscount.toLocaleString('en-IN')}</span>
                </div>
                <div className="py-2.5 flex justify-between items-center">
                  <span className="font-semibold text-slate-700">Cost of Goods Sold (COGS)</span>
                  <span className="font-mono text-slate-650 font-bold">₹{salesSummary.costOfGoodsSold.toLocaleString('en-IN')}</span>
                </div>
                <div className="py-2.5 flex justify-between items-center bg-emerald-50 px-2 rounded border border-emerald-100 mt-2">
                  <span className="font-extrabold text-emerald-900">Realized Net Profit</span>
                  <span className="font-mono font-extrabold text-emerald-800">
                    ₹{salesSummary.netProfit.toLocaleString('en-IN')}
                  </span>
                </div>
              </div>
            </div>

            {/* Inventory Valuation Insights */}
            <div className="card">
              <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4 flex items-center gap-1.5">
                <FileSpreadsheet size={15} className="text-emerald-600" />
                <span>Inventory Valuation (Surat Cluster)</span>
              </h3>
              <div className="divide-y divide-slate-100 text-xs space-y-2">
                <div className="py-2.5 flex justify-between items-center">
                  <span className="font-bold text-slate-800">Total Cataloged Items</span>
                  <span className="font-mono text-slate-650">{valuationData.totalItems} SKUs</span>
                </div>
                <div className="py-2.5 flex justify-between items-center">
                  <span className="font-bold text-slate-800">Total Stock quantity</span>
                  <span className="font-mono text-slate-655">{valuationData.totalStock} units</span>
                </div>
                <div className="py-2.5 flex justify-between items-center">
                  <span className="font-bold text-slate-800">Cost Price Valuation</span>
                  <span className="font-mono text-slate-650">
                    ₹{valuationData.costValuation.toLocaleString('en-IN')}
                  </span>
                </div>
                <div className="py-2.5 flex justify-between items-center">
                  <span className="font-bold text-slate-800">Selling Price Valuation</span>
                  <span className="font-mono text-slate-650">
                    ₹{valuationData.sellingValuation.toLocaleString('en-IN')}
                  </span>
                </div>
                <div className="py-2 flex justify-between items-center bg-emerald-50 px-2 rounded border border-emerald-100 mt-2">
                  <span className="font-extrabold text-emerald-900">Potential Gross Profit</span>
                  <span className="font-mono font-extrabold text-emerald-800">
                    ₹{valuationData.potentialProfit.toLocaleString('en-IN')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* GST Compliance Tab content */
        <div className="space-y-6 animate-fadeIn">
          {!hasGstAccess ? (
            /* Premium Gate block if the subscription doesn't have it */
            <div className="flex flex-col items-center justify-center bg-white border border-slate-200 rounded-2xl p-12 max-w-xl mx-auto text-center space-y-6 shadow-sm my-6">
              <div className="w-16 h-16 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-550 shadow-md">
                <Lock size={32} className="animate-bounce" />
              </div>
              <div className="space-y-2">
                <span className="text-[10px] text-amber-600 font-bold uppercase tracking-wider bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full">
                  Premium Module Locked
                </span>
                <h2 className="text-2xl font-extrabold text-slate-850 tracking-tight">
                  Unlock GST Governance Engine
                </h2>
                <p className="text-sm text-slate-500 leading-relaxed font-light">
                  Your current subscription tier does not include the advanced GST ledger serialization or quarter-wise GSTR-1 audits.
                </p>
              </div>
              <div className="w-full bg-slate-50 border border-slate-150 p-5 rounded-2xl text-left text-xs space-y-3">
                <span className="font-bold text-slate-700 block border-b border-slate-200 pb-2">
                  Unlocked on higher tiers:
                </span>
                <ul className="space-y-2.5 text-slate-550 font-medium">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    <span>Generate CGST, SGST, and IGST breakdowns for direct financial audits.</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    <span>Download automated, compliance-ready GSTR-1 spreadsheet ledgers.</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    <span>Monitor dynamic, dynamic tax contribution distribution charts.</span>
                  </li>
                </ul>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed font-light">
                Please contact your SaaS system administrator at{' '}
                <span className="font-bold text-emerald-650">support@threads.com</span> to upgrade your license tier and unlock these features.
              </p>
            </div>
          ) : (
            <>
              {/* GST Stats summary panel */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Total Tax Collected */}
                <div className="card flex justify-between items-start">
                  <div className="space-y-1">
                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                      Total GST Tax Collected
                    </span>
                    <p className="text-xl font-bold text-emerald-800 font-mono">
                      ₹{auditSummary.totalGst.toLocaleString('en-IN')}
                    </p>
                  </div>
                  <span className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 font-bold rounded flex items-center gap-0.5">
                    <Percent size={10} />
                    <span>Ratio Active</span>
                  </span>
                </div>

                {/* CGST */}
                <div className="card flex justify-between items-start">
                  <div className="space-y-1">
                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                      CGST Collected
                    </span>
                    <p className="text-xl font-bold text-slate-800 font-mono">
                      ₹{auditSummary.cgst.toLocaleString('en-IN')}
                    </p>
                  </div>
                  <span className="text-[10px] text-slate-400 font-mono font-medium">Central Tax</span>
                </div>

                {/* SGST */}
                <div className="card flex justify-between items-start">
                  <div className="space-y-1">
                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                      SGST Collected
                    </span>
                    <p className="text-xl font-bold text-slate-800 font-mono">
                      ₹{auditSummary.sgst.toLocaleString('en-IN')}
                    </p>
                  </div>
                  <span className="text-[10px] text-slate-400 font-mono font-medium">State Tax</span>
                </div>

                {/* IGST */}
                <div className="card flex justify-between items-start">
                  <div className="space-y-1">
                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                      IGST Collected
                    </span>
                    <p className="text-xl font-bold text-slate-800 font-mono">
                      ₹{auditSummary.igst.toLocaleString('en-IN')}
                    </p>
                  </div>
                  <span className="text-[10px] text-slate-400 font-mono font-medium">Integrated Tax</span>
                </div>
              </div>

              {/* Main GST Audit matrix */}
              <div className="card border border-slate-150 shadow-sm">
                <div className="flex justify-between items-center mb-4 border-b border-slate-100 pb-3">
                  <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2">
                    <FileSpreadsheet className="text-emerald-600" size={16} />
                    <span>GSTR-1 Slab Wise Return Audit Summary ({selectedQuarter})</span>
                  </h2>
                  <span className="text-[10px] font-bold px-2 py-0.5 bg-emerald-50 text-emerald-800 border border-emerald-100 rounded">
                    E-INVOICING ACTIVE
                  </span>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="border-b border-slate-150 text-slate-500 font-bold uppercase tracking-wider bg-slate-50">
                        <th className="py-2.5 px-3 text-center w-12">S.No.</th>
                        <th className="py-2.5 px-3">GST Slab</th>
                        <th className="py-2.5 px-2 text-right">Taxable Value (₹)</th>
                        <th className="py-2.5 px-2 text-right">CGST (₹)</th>
                        <th className="py-2.5 px-2 text-right">SGST (₹)</th>
                        <th className="py-2.5 px-2 text-right">IGST (₹)</th>
                        <th className="py-2.5 px-3 text-right">Total GST Tax (₹)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {gstSlabAudit.map((slab, idx) => (
                        <tr key={slab.rate} className="hover:bg-slate-50/50">
                          <td className="py-3 px-3 text-center text-slate-500 font-medium">{idx + 1}</td>
                          <td className="py-3 px-3 font-bold text-slate-850">{slab.rate}% Category</td>
                          <td className="py-3 px-2 text-right font-mono font-medium">
                            ₹{slab.taxableValue.toLocaleString('en-IN')}
                          </td>
                          <td className="py-3 px-2 text-right font-mono text-slate-600">
                            ₹{slab.cgst.toLocaleString('en-IN')}
                          </td>
                          <td className="py-3 px-2 text-right font-mono text-slate-600">
                            ₹{slab.sgst.toLocaleString('en-IN')}
                          </td>
                          <td className="py-3 px-2 text-right font-mono text-slate-600">
                            ₹{slab.igst.toLocaleString('en-IN')}
                          </td>
                          <td className="py-3 px-3 text-right font-mono font-bold text-emerald-800">
                            ₹{slab.totalGst.toLocaleString('en-IN')}
                          </td>
                        </tr>
                      ))}
                      {/* Total Aggregate row */}
                      <tr className="bg-slate-50 font-bold border-t border-slate-200">
                        <td colSpan={2} className="py-3 px-3 uppercase text-slate-900">Total Audit Summary</td>
                        <td className="py-3 px-2 text-right font-mono text-slate-900">
                          ₹{auditSummary.netSales.toLocaleString('en-IN')}
                        </td>
                        <td className="py-3 px-2 text-right font-mono text-slate-900">
                          ₹{auditSummary.cgst.toLocaleString('en-IN')}
                        </td>
                        <td className="py-3 px-2 text-right font-mono text-slate-900">
                          ₹{auditSummary.sgst.toLocaleString('en-IN')}
                        </td>
                        <td className="py-3 px-2 text-right font-mono text-slate-900">
                          ₹{auditSummary.igst.toLocaleString('en-IN')}
                        </td>
                        <td className="py-3 px-3 text-right font-mono text-emerald-950">
                          ₹{auditSummary.totalGst.toLocaleString('en-IN')}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Tax Slab Distribution shares chart */}
              <div className="card max-w-xl">
                <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4">
                  Tax Category Contribution Distribution
                </h3>
                <div className="space-y-3.5 pt-1">
                  {gstSlabAudit.map((slab) => {
                    const contribution =
                      auditSummary.totalGst > 0 ? (slab.totalGst / auditSummary.totalGst) * 100 : 0;
                    return (
                      <div key={slab.rate} className="space-y-1">
                        <div className="flex justify-between text-xs font-semibold">
                          <span className="text-slate-600">{slab.rate}% GST Tax Slab</span>
                          <span className="text-slate-850 font-bold">{contribution.toFixed(1)}%</span>
                        </div>
                        <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden">
                          <div
                            className="bg-emerald-600 h-full rounded-full transition-all duration-500"
                            style={{ width: `${contribution}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
